import React, { useRef, useState } from 'react';
import { cx } from '../cx';

// D-09 §15.17 — 파일 업로드(드롭존형). 드래그&드롭 + 클릭 (§2-4)
// value/onChange 로 제어. 트리거는 div role="presentation" 이다 — renderEmpty 안에 버튼을 넣어도
// button-in-button 이 되지 않게 하려는 것이고, 대신 tabIndex/onKeyDown 으로 포커스·Enter/Space 를 직접 복원한다.
// state: idle / dragover / uploading / error / filled (배경은 §3-7 색토큰 확정 시 교체 — 현재 중립 토큰 임시).
export interface UploadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  accept?: string;
  multiple?: boolean;
  maxSizeMB?: number;
  maxCount?: number;
  value?: File[];
  onChange: (files: File[]) => void;
  hint?: string;
  disabled?: boolean;
  uploading?: boolean;
  /** 비어 있을 때 트리거 내부에 렌더할 내용 — 없으면 기본 문구 */
  renderEmpty?: () => React.ReactNode;
}

export function Upload({
  accept,
  multiple = true,
  maxSizeMB,
  maxCount,
  value = [],
  onChange,
  hint,
  disabled,
  uploading,
  renderEmpty,
  className = '',
  id,
  ...props
}: UploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState('');

  const validate = (incoming: File[]): File[] | null => {
    let next = multiple ? [...value, ...incoming] : incoming.slice(0, 1);
    if (maxSizeMB != null) {
      const over = next.find((f) => f.size > maxSizeMB * 1024 * 1024);
      if (over) {
        setError(`파일 크기는 ${maxSizeMB}MB 이하여야 합니다`);
        return null;
      }
    }
    if (maxCount != null && next.length > maxCount) {
      setError(`최대 ${maxCount}개까지 첨부할 수 있습니다`);
      next = next.slice(0, maxCount);
    } else {
      setError('');
    }
    return next;
  };

  const addFiles = (files: File[]) => {
    if (disabled || files.length === 0) return;
    const next = validate(files);
    if (next) onChange(next);
  };

  const state = disabled
    ? 'disabled'
    : uploading
      ? 'uploading'
      : drag
        ? 'dragover'
        : value.length > 0
          ? 'filled'
          : 'idle';

  return (
    <div className={cx('dropzone', `is-${state}`, className)} {...props}>
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        hidden
        onChange={(e) => {
          addFiles(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      <div
        className="dropzone-trigger"
        role="presentation"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          // renderEmpty 안의 포커스 가능한 자식(버튼 등)에서 올라온 키는 무시 — 그 자식 동작만 실행돼야 한다.
          if (disabled || e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled && !uploading) setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          addFiles(Array.from(e.dataTransfer.files ?? []));
        }}
      >
        {uploading ? (
          <span className="dropzone-title">업로드 중…</span>
        ) : value.length === 0 && renderEmpty ? (
          renderEmpty()
        ) : (
          // 제목↔힌트 8 을 블록 갭 24 와 분리한다 — renderEmpty 를 줄 때도 같은 래퍼를 쓴다(D-09 §15.17)
          <div className="dropzone-text">
            <span className="dropzone-title">
              {value.length > 0 ? `${value.length}개 선택됨` : '파일을 끌어다 놓거나 클릭하세요'}
            </span>
            {hint && <span className="dropzone-hint">{hint}</span>}
          </div>
        )}
      </div>
      {value.length > 0 && (
        <ul className="dropzone-list">
          {value.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              <span className="dropzone-name">{f.name}</span>
              <button
                type="button"
                className="dropzone-remove"
                aria-label={`${f.name} 제거`}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <span className="help">{error}</span>}
    </div>
  );
}
